import React, { useState, useEffect, useCallback } from 'react';
import { RotateCcw } from 'lucide-react';
import { useTranslation } from '../../contexts/TranslationContext';
import { Resource, UIComponent } from '../../types';

interface CountObjectsComponentProps {
  resource: Resource;
  component: UIComponent;
  onAction?: (action: string, data?: any) => void;
}

interface ObjectConfig {
  type: string;
  count: number;
  color: string;
}

interface GeneratedObject {
  id: number;
  x: number;
  y: number;
  counted: boolean;
  type: string;
  color: string;
  groupIndex: number;
}

export const CountObjectsComponent: React.FC<CountObjectsComponentProps> = ({ resource, component, onAction }) => {
  const { t } = useTranslation();
  const [objects, setObjects] = useState<GeneratedObject[]>([]);
  const [count, setCount] = useState<number>(0);
  const [question, setQuestion] = useState<string>('');

  // Get settings from resource or component props
  const randomize = resource.randomize !== undefined ? resource.randomize : true;
  const touchFeedback = resource.touch_feedback || 'ping';
  const showCount = resource.showCount !== undefined ? resource.showCount : true;
  const allowInteraction = resource.allowInteraction !== undefined ? resource.allowInteraction : true;

  // Check if using new object configuration structure
  const hasObjectConfig = Array.isArray(resource.objects) && resource.objects.length > 0;
  
  // Get total object count
  const getTotalObjectCount = useCallback(() => {
    if (hasObjectConfig) {
      return resource.objects.reduce((total: number, obj: ObjectConfig) => total + obj.count, 0);
    }
    return resource.objects || 20;
  }, [hasObjectConfig, resource.objects]);

  const generateObjects = useCallback(() => {
    let newObjects: GeneratedObject[] = [];
    let objectId = 0;

    if (hasObjectConfig) {
      // Use new configuration structure with multiple object types
      resource.objects.forEach((objConfig: ObjectConfig, groupIndex: number) => {
        for (let i = 0; i < objConfig.count; i++) {
          newObjects.push({
            id: objectId++,
            x: randomize ? Math.random() * 200 : (objectId % 5) * 40 + 20,
            y: randomize ? Math.random() * 150 : Math.floor(objectId / 5) * 30 + 20,
            counted: false,
            type: objConfig.type,
            color: objConfig.color,
            groupIndex
          });
        }
      });
    } else {
      // Use legacy configuration structure
      const totalCount = getTotalObjectCount();
      newObjects = Array.from({ length: totalCount }, (_, i) => ({
        id: i,
        x: randomize ? Math.random() * 200 : (i % 5) * 40 + 20,
        y: randomize ? Math.random() * 150 : Math.floor(i / 5) * 30 + 20,
        counted: false,
        type: 'circle',
        color: 'blue',
        groupIndex: 0
      }));
    }

    setObjects(newObjects);
    setCount(0);
  }, [hasObjectConfig, resource.objects, randomize, getTotalObjectCount]);

  const handleObjectClick = (objectId: number) => {
    if (!allowInteraction) return;
    
    setObjects(prev => prev.map(obj => 
      obj.id === objectId ? { ...obj, counted: !obj.counted } : obj
    ));
    setCount(prev => {
      const newCount = objects.find(obj => obj.id === objectId)?.counted ? prev - 1 : prev + 1;
      onAction?.('object_clicked', { objectId, count: newCount });
      return newCount;
    });
  };

  const getTouchFeedbackClass = () => {
    switch (touchFeedback) {
      case 'ping':
        return 'animate-ping';
      case 'pulse':
        return 'animate-pulse';
      case 'bounce':
        return 'animate-bounce';
      case 'shake':
        return 'animate-shake';
      default:
        return '';
    }
  };



  const renderObject = (object: GeneratedObject) => {
    const baseClasses = 'absolute transition-all';
    const sizeClasses = 'w-4 h-4';
    
    if (object.counted) {
      const countedClasses = `${baseClasses} ${sizeClasses} border-2 ${touchFeedback !== 'none' ? getTouchFeedbackClass() : ''}`;
      
      switch (object.type) {
        case 'square':
          return (
            <div
              className={`${countedClasses} bg-green-500 border-green-600`}
              style={{ 
                left: `${object.x}px`, 
                top: `${object.y}px`,
                backgroundColor: object.color,
                borderColor: object.color
              }}
            />
          );
        case 'triangle':
          return (
            <div
              className={`${countedClasses} bg-green-500 border-green-600`}
              style={{ 
                left: `${object.x}px`, 
                top: `${object.y}px`,
                backgroundColor: object.color,
                borderColor: object.color,
                clipPath: 'polygon(50% 0%, 0% 100%, 100% 100%)'
              }}
            />
          );
        case 'star':
          return (
            <svg
              className={`${countedClasses} bg-green-500 border-green-600`}
              style={{ 
                left: `${object.x}px`, 
                top: `${object.y}px`,
                fill: object.color,
                stroke: object.color
              }}
              viewBox="0 0 24 24"
            >
              <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
            </svg>
          );
        case 'circle':
        default:
          return (
            <div
              className={`${countedClasses} bg-green-500 border-green-600 rounded-full`}
              style={{ 
                left: `${object.x}px`, 
                top: `${object.y}px`,
                backgroundColor: object.color,
                borderColor: object.color
              }}
            />
          );
      }
    } else {
      const uncountedClasses = `${baseClasses} ${sizeClasses} border-2 hover:opacity-80`;
      
      switch (object.type) {
        case 'square':
          return (
            <div
              className={`${uncountedClasses}`}
              style={{ 
                left: `${object.x}px`, 
                top: `${object.y}px`,
                backgroundColor: object.color,
                borderColor: object.color
              }}
            />
          );
        case 'triangle':
          return (
            <div
              className={`${uncountedClasses}`}
              style={{ 
                left: `${object.x}px`, 
                top: `${object.y}px`,
                backgroundColor: object.color,
                borderColor: object.color,
                clipPath: 'polygon(50% 0%, 0% 100%, 100% 100%)'
              }}
            />
          );
        case 'star':
          return (
            <svg
              className={`${uncountedClasses}`}
              style={{ 
                left: `${object.x}px`, 
                top: `${object.y}px`,
                fill: object.color,
                stroke: object.color
              }}
              viewBox="0 0 24 24"
            >
              <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
            </svg>
          );
        case 'circle':
        default:
          return (
            <div
              className={`${uncountedClasses} rounded-full`}
              style={{ 
                left: `${object.x}px`, 
                top: `${object.y}px`,
                backgroundColor: object.color,
                borderColor: object.color
              }}
            />
          );
      }
    }
  };

  useEffect(() => {
    generateObjects();
    // Set question if provided
    if (resource.question) {
      setQuestion(resource.question);
    }
  }, [generateObjects, resource.question]);

  // Reset component state when resource changes (for quiz navigation)
  useEffect(() => {
    generateObjects();
  }, [resource.id, resource.objects, generateObjects]);

  const totalObjects = getTotalObjectCount();

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-semibold text-gray-900">{t('countObjects.countObjects')}</h3>
        {allowInteraction && (
          <div className="flex items-center space-x-2">
            <button
              onClick={generateObjects}
              className="p-1 text-gray-600 hover:bg-gray-50 rounded"
            >
              <RotateCcw className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>
      
      {question && (
        <div className="mb-3 p-2 bg-blue-50 rounded border border-blue-200">
          <p className="text-sm font-medium text-blue-800">{question}</p>
        </div>
      )}
      
      <div className="relative w-full h-40 bg-gray-50 rounded border overflow-hidden">
        {objects.map((object) => (
          <div
            key={object.id}
            onClick={() => allowInteraction && handleObjectClick(object.id)}
            className={allowInteraction ? 'cursor-pointer' : ''}
          >
            {renderObject(object)}
          </div>
        ))}
      </div>
      
      {showCount && (
        <div className="mt-3 text-sm text-gray-600">
          {t('countObjects.counted')}: {count} / {totalObjects}
        </div>
      )}
    </div>
  );
};
